import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Tag,
  Megaphone,
  Gift,
  Star,
  Loader2
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const EVENT_TYPES = {
  promotion: { icon: Tag, color: 'bg-green-500', label: 'Promotion' },
  campaign: { icon: Megaphone, color: 'bg-blue-500', label: 'Campaign' },
  festival: { icon: Gift, color: 'bg-purple-500', label: 'Festival' },
  flyer: { icon: Star, color: 'bg-orange-500', label: 'Weekly Flyer' }
};

export default function PromotionalCalendarWidget({ showHeader = true }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const data = await apiRequest(`/sales/promotional-calendar?year=${year}&month=${month}`);
      setEvents(data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents(getMockEvents());
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getEventsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr || (e.start_date <= dateStr && e.end_date >= dateStr));
  };

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const upcomingEvents = events
    .filter(e => new Date(e.start_date || e.date) >= new Date())
    .slice(0, 4);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="promotional-calendar-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <CalendarIcon size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Promotional Calendar</h3>
              <p className="text-xs text-slate-500">{events.length} events this month</p>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            <Plus size={16} />
          </Button>
        </div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <Button variant="ghost" size="sm" onClick={goToPrevMonth}>
          <ChevronLeft size={16} />
        </Button>
        <span className="font-semibold text-slate-900">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </span>
        <Button variant="ghost" size="sm" onClick={goToNextMonth}>
          <ChevronRight size={16} />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="p-3 border-b border-slate-100">
        <div className="grid grid-cols-7 gap-1 text-center mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className="text-xs font-medium text-slate-400 py-1">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: startingDay }).map((_, i) => (
            <div key={`empty-${i}`} className="h-8" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayEvents = getEventsForDay(day);
            const isToday = new Date().getDate() === day && 
                           new Date().getMonth() === currentDate.getMonth() &&
                           new Date().getFullYear() === currentDate.getFullYear();
            
            return (
              <div
                key={day}
                onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                className={`h-8 flex flex-col items-center justify-center rounded-lg text-sm cursor-pointer transition-colors relative
                  ${isToday ? 'bg-red-500 text-white font-bold' : 'hover:bg-slate-100'}
                  ${selectedDay === day ? 'ring-2 ring-red-500' : ''}
                `}
              >
                {day}
                {dayEvents.length > 0 && (
                  <div className="absolute bottom-0.5 flex gap-0.5">
                    {dayEvents.slice(0, 3).map((e, idx) => (
                      <div key={idx} className={`w-1 h-1 rounded-full ${EVENT_TYPES[e.type]?.color || 'bg-slate-400'}`} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="flex-1 overflow-y-auto p-3">
        <p className="text-xs font-medium text-slate-500 mb-2">UPCOMING</p>
        <div className="space-y-2">
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No upcoming events</p>
          ) : (
            upcomingEvents.map((event, idx) => {
              const eventType = EVENT_TYPES[event.type] || EVENT_TYPES.promotion;
              const Icon = eventType.icon;
              return (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <div className={`w-8 h-8 rounded-lg ${eventType.color} flex items-center justify-center`}>
                    <Icon size={14} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{event.name}</p>
                    <p className="text-xs text-slate-500">{event.date_display || event.date}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{eventType.label}</Badge>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function getMockEvents() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  
  return [
    { name: "Diwali Mega Sale", type: "festival", start_date: `${year}-${month}-15`, end_date: `${year}-${month}-30`, date_display: "Mar 15-30" },
    { name: "Weekly Flyer Week 12", type: "flyer", date: `${year}-${month}-18`, start_date: `${year}-${month}-18`, end_date: `${year}-${month}-24`, date_display: "Mar 18-24" },
    { name: "Snacks BOGO Campaign", type: "campaign", date: `${year}-${month}-20`, start_date: `${year}-${month}-20`, end_date: `${year}-${month}-27`, date_display: "Mar 20-27" },
    { name: "Easter Specials", type: "festival", date: `${year}-${month}-28`, start_date: `${year}-${month}-28`, end_date: `${year}-04-05`, date_display: "Mar 28 - Apr 5" },
    { name: "Fresh Produce Week", type: "promotion", date: `${year}-${month}-25`, start_date: `${year}-${month}-25`, end_date: `${year}-${month}-31`, date_display: "Mar 25-31" },
  ];
}
